import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useSendNotification } from '../hooks/useSendNotification';
import { useChannelCatalog } from '../hooks/useChannelCatalog';
import { sendNotificationSchema } from '../schema/notificationCenterSchema';
import type { SendNotificationFormValues } from '../schema/notificationCenterSchema';
import type { ApiError } from '../types/notificationCenter';

interface NotificationSendDialogProps {
  onClose: () => void;
  onSuccess?: (toast: string) => void;
  onError?: (toast: string) => void;
}

const SEVERITY_OPTIONS = ['INFO', 'NOTICE', 'WARN', 'CRITICAL'] as const;

/** 채널별 대상 입력 placeholder i18n 키 (§12.3) */
const TARGET_HINT_KEY: Record<'EMAIL' | 'WEBHOOK' | 'SMS' | 'PUSH', string> = {
  EMAIL: 'notificationCenter.send.field.targetHintEmail',
  WEBHOOK: 'notificationCenter.send.field.targetHintWebhook',
  SMS: 'notificationCenter.send.field.targetHintSms',
  PUSH: 'notificationCenter.send.field.targetHintPush',
};

/** 폼 필드 공통 레이블 */
function FieldLabel({ htmlFor, required, children }: { htmlFor: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: 'block',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        color: 'var(--color-text-secondary)',
        marginBottom: 'var(--spacing-1)',
      }}
    >
      {children}
      {required && (
        <span style={{ color: 'var(--color-danger)', marginLeft: 2 }} aria-hidden="true">
          {' *'}
        </span>
      )}
    </label>
  );
}

/** 폼 인라인 오류 메시지 */
function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <span
      id={id}
      role="alert"
      style={{
        display: 'block',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-danger)',
        marginTop: 'var(--spacing-1)',
      }}
    >
      {message}
    </span>
  );
}

/**
 * NotificationSendDialog — 알림 발송 폼 다이얼로그
 * role=dialog · aria-modal=true · focus trap · ESC 닫기
 * react-hook-form + zod resolver
 */
export function NotificationSendDialog({ onClose, onSuccess, onError }: NotificationSendDialogProps) {
  const { t } = useTranslation();
  const sendMutation = useSendNotification();
  const channelQuery = useChannelCatalog();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SendNotificationFormValues>({
    resolver: zodResolver(sendNotificationSchema),
    defaultValues: {
      channel: 'EMAIL',
      target: '',
      title: '',
      text: '',
      severity: 'INFO',
    },
  });

  const selectedChannel = watch('channel');

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  const onSubmit = useCallback(
    (values: SendNotificationFormValues) => {
      const body = {
        channel: values.channel,
        target: values.target,
        title: values.title || undefined,
        text: values.text,
        severity: values.severity,
        cc: values.cc
          ? values.cc.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined,
      };

      sendMutation.mutate(body as Parameters<typeof sendMutation.mutate>[0], {
        onSuccess: () => {
          onSuccess?.(t('notificationCenter.toast.sendStarted'));
          onClose();
        },
        onError: (err) => {
          const apiErr = err as unknown as ApiError;
          const code = apiErr?.code ?? '';
          const errKey = `notificationCenter.error.${code}`;
          const msg = t(errKey, { defaultValue: t('notificationCenter.toast.sendFailed') });
          onError?.(msg);
        },
      });
    },
    [sendMutation, onSuccess, onError, onClose, t]
  );

  const isFormDisabled = isSubmitting || sendMutation.isPending;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-dialog-title"
        aria-describedby="send-dialog-desc"
        onKeyDown={handleKeyDown}
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: 'min(520px, 94vw)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-4)' }}>
          <h2
            id="send-dialog-title"
            style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0 }}
          >
            {t('notificationCenter.send.title')}
          </h2>
          <button
            type="button"
            aria-label={t('notificationCenter.send.button.cancel')}
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-xl)',
              cursor: 'pointer',
              width: 32,
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-md)',
            }}
          >
            ✕
          </button>
        </div>

        <p
          id="send-dialog-desc"
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-5)',
          }}
        >
          {channelQuery.data && channelQuery.data.length > 0
            ? channelQuery.data
                .filter((c) => c.enabled)
                .map((c) => c.labelKo)
                .join(' / ')
            : '이메일 / Slack/Teams'}
          {' 채널로 알림을 발송합니다'}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>

            {/* 채널 선택 */}
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                {t('notificationCenter.send.field.channel')}
                <span style={{ color: 'var(--color-danger)', marginLeft: 2 }} aria-hidden="true"> *</span>
              </legend>
              <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
                {(['EMAIL', 'WEBHOOK', 'SMS', 'PUSH'] as const).map((ch) => (
                  <label
                    key={ch}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    <input
                      type="radio"
                      value={ch}
                      {...register('channel')}
                      disabled={isFormDisabled}
                      style={{ cursor: 'pointer' }}
                    />
                    {t(`notificationCenter.channel.${ch}`)}
                  </label>
                ))}
              </div>
              <FieldError id="channel-error" message={errors.channel?.message} />
            </fieldset>

            {/* 대상 */}
            <div>
              <FieldLabel htmlFor="notif-target" required>
                {t('notificationCenter.send.field.target')}
              </FieldLabel>
              <input
                id="notif-target"
                type="text"
                placeholder={t(TARGET_HINT_KEY[selectedChannel])}
                {...register('target')}
                disabled={isFormDisabled}
                aria-invalid={!!errors.target}
                aria-describedby={errors.target ? 'target-error' : undefined}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: `1px solid ${errors.target ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                }}
              />
              <FieldError id="target-error" message={errors.target?.message} />
            </div>

            {/* 참조 (이메일 전용) */}
            {selectedChannel === 'EMAIL' && (
              <div>
                <FieldLabel htmlFor="notif-cc">
                  {t('notificationCenter.send.field.cc')}
                </FieldLabel>
                <input
                  id="notif-cc"
                  type="text"
                  placeholder="쉼표로 구분 (예: a@skax.com, b@skax.com)"
                  {...register('cc')}
                  disabled={isFormDisabled}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                />
              </div>
            )}

            {/* 제목 */}
            <div>
              <FieldLabel htmlFor="notif-title">
                {t('notificationCenter.send.field.subject')}
              </FieldLabel>
              <input
                id="notif-title"
                type="text"
                placeholder="알림 제목 (선택)"
                {...register('title')}
                disabled={isFormDisabled}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                }}
              />
            </div>

            {/* 본문 */}
            <div>
              <FieldLabel htmlFor="notif-text" required>
                {t('notificationCenter.send.field.body')}
              </FieldLabel>
              <textarea
                id="notif-text"
                rows={4}
                placeholder="알림 본문을 입력하세요"
                {...register('text')}
                disabled={isFormDisabled}
                aria-invalid={!!errors.text}
                aria-describedby={errors.text ? 'text-error' : undefined}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: `1px solid ${errors.text ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                  resize: 'vertical',
                }}
              />
              <FieldError id="text-error" message={errors.text?.message} />
            </div>

            {/* 심각도 */}
            <div>
              <FieldLabel htmlFor="notif-severity">
                {t('notificationCenter.send.field.severity')}
              </FieldLabel>
              <select
                id="notif-severity"
                {...register('severity')}
                disabled={isFormDisabled}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                  cursor: 'pointer',
                }}
              >
                {SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`notificationCenter.severity.${s}`)} ({s})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 버튼 영역 */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-2)',
              justifyContent: 'flex-end',
              marginTop: 'var(--spacing-6)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isFormDisabled}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: isFormDisabled ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('notificationCenter.send.button.cancel')}
            </button>
            <button
              type="submit"
              disabled={isFormDisabled}
              style={{
                padding: 'var(--spacing-2) var(--spacing-5)',
                background: isFormDisabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                color: isFormDisabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isFormDisabled ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: isFormDisabled ? 0.6 : 1,
              }}
            >
              {isFormDisabled ? '...' : t('notificationCenter.send.button.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
